import type {
	OrganizationAssignmentStateFilter,
	OrganizationAssignmentType,
	OrganizationLocationType,
	OrganizationUnitStatus,
} from '@today/types'
import { apiClient } from './api.client'
import type { Paged } from './customer.service'

/**
 * Organization API client (TO-389 organization-admin-ui). Shapes mirror the
 * organization-service DTOs (TO-411 units/types, TO-387 locations, TO-412
 * assignments); dates arrive as ISO strings.
 */

export interface OrgUnitDTO {
	id: string
	code: string
	name: string
	shortName: string | null
	externalId: string | null
	typeId: string
	typeCode: string
	parentId: string | null
	parentCode: string | null
	status: OrganizationUnitStatus
	isPhysical: boolean
	institutionCode: string | null
	countryCode: string | null
	timezone: string | null
	metadata: Record<string, unknown>
	validFrom: string | null
	validTo: string | null
	createdAt: string
	updatedAt: string
}

export interface OrgTypeDTO {
	id: string
	code: string
	name: string
	isPhysical: boolean
	canHaveUsers: boolean
	sortOrder: number
	metadata: Record<string, unknown>
	allowedParentTypeCodes: string[] | null
}

export interface OrgLocationDTO {
	id: string
	unitId: string
	locationType: OrganizationLocationType
	addressLine1: string
	addressLine2: string | null
	postalCode: string | null
	city: string
	province: string | null
	region: string | null
	countryCode: string
	latitude: number | null
	longitude: number | null
	phone: string | null
	email: string | null
}

export interface OrgAssignmentDTO {
	id: string
	userId: string
	unitId: string
	unitCode: string
	unitName: string
	assignmentType: OrganizationAssignmentType
	isPrimary: boolean
	validFrom: string
	validTo: string | null
}

export interface OrgUserDTO {
	userId: string
	displayName: string
	email: string
}

const EMPTY_PAGE = <T>(page: number, limit: number): Paged<T> => ({ data: [], total: 0, page, limit, totalPages: 0 })

/** GET a paginated list; an empty result is `200 {data:[],total:0}` (TO-632, AD-21.7). */
const getPage = async <T>(url: string, params: Record<string, string>): Promise<Paged<T>> => {
	const response = await apiClient.get<Paged<T> | ''>(url, { params })
	const page = Number(params.page ?? 1)
	const limit = Number(params.limit ?? 20)
	return response.data ? response.data : EMPTY_PAGE<T>(page, limit)
}

/** Every page of a list at limit=100, sequentially, in the API's order. */
const allPages = async <T>(url: string, params: Record<string, string> = {}): Promise<T[]> => {
	const all: T[] = []
	for (let page = 1; ; page++) {
		const result = await getPage<T>(url, { ...params, page: String(page), limit: '100' })
		all.push(...result.data)
		if (page >= result.totalPages) return all
	}
}

const enc = encodeURIComponent
const unit = (id: string) => `/organization/units/${enc(id)}`

export const organizationService = {
	/** One page of every tenant unit (no filter), ordered by code. */
	unitPage: (page: number) => getPage<OrgUnitDTO>('/organization/units', { page: String(page), limit: '100' }),
	getUnit: async (id: string) => (await apiClient.get<OrgUnitDTO>(unit(id))).data,
	createUnit: async (body: Record<string, unknown>) =>
		(await apiClient.post<OrgUnitDTO>('/organization/units', body)).data,
	updateUnit: async (id: string, body: Record<string, unknown>) =>
		(await apiClient.patch<OrgUnitDTO>(unit(id), body)).data,
	moveUnit: async (id: string, parentCode: string | null) =>
		(await apiClient.post<OrgUnitDTO>(`${unit(id)}/move`, { parentCode })).data,

	locations: async (unitId: string) =>
		(await apiClient.get<{ data: OrgLocationDTO[] }>(`${unit(unitId)}/locations`)).data.data,
	createLocation: async (unitId: string, body: Record<string, unknown>) =>
		(await apiClient.post<OrgLocationDTO>(`${unit(unitId)}/locations`, body)).data,
	updateLocation: async (unitId: string, locationId: string, body: Record<string, unknown>) =>
		(await apiClient.patch<OrgLocationDTO>(`${unit(unitId)}/locations/${enc(locationId)}`, body)).data,
	deleteLocation: async (unitId: string, locationId: string): Promise<void> => {
		await apiClient.delete(`${unit(unitId)}/locations/${enc(locationId)}`)
	},

	/** Every assignment of a unit in `state`, all pages. */
	assignments: (unitId: string, state: OrganizationAssignmentStateFilter) =>
		allPages<OrgAssignmentDTO>('/organization/assignments', {
			'filters[unitId]': unitId,
			'filters[state]': state,
		}),
	createAssignment: async (body: Record<string, unknown>) =>
		(await apiClient.post<OrgAssignmentDTO>('/organization/assignments', body)).data,
	endAssignment: async (id: string) =>
		(await apiClient.post<OrgAssignmentDTO>(`/organization/assignments/${enc(id)}/end`, {})).data,
	makePrimary: async (id: string) =>
		(await apiClient.post<OrgAssignmentDTO>(`/organization/assignments/${enc(id)}/primary`, {})).data,

	/** Every type, ordered by sortOrder then code. */
	types: () => allPages<OrgTypeDTO>('/organization/types'),
	createType: async (body: Record<string, unknown>) =>
		(await apiClient.post<OrgTypeDTO>('/organization/types', body)).data,
	updateType: async (id: string, body: Record<string, unknown>) =>
		(await apiClient.patch<OrgTypeDTO>(`/organization/types/${enc(id)}`, body)).data,
	deleteType: async (id: string): Promise<void> => {
		await apiClient.delete(`/organization/types/${enc(id)}`)
	},

	/** The tenant's users for the assignee picker (needs `user.read`). */
	users: (slug: string) => allPages<OrgUserDTO>(`/t/${enc(slug)}/admin/users`),
}
